/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState } from 'react';
import { KeyboardDatePicker, DatePicker } from '@material-ui/pickers';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import { makeStyles } from '@material-ui/core/styles';
import { format as dfFormat, isValid as isDateValid } from 'date-fns';
import { isNil, omitBy } from 'lodash';
import clsx from 'clsx';
import Event from '@material-ui/icons/Event';
import { ZERO_DATE, DEFAULT_DATE_FORMAT } from 'app/constants/DateTimeFormat';
import TextField from 'components/TextField';
import { toDateString, isValid } from 'helpers/Date';
import { usePharmacyTimezone } from 'app/hooks';
import useEnhanceDateInput from './useEnhanceDateInput';
import DateSegmentField from './DateSegmentField';

const useStyles = makeStyles((theme) => ({
  icon: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: '2.4rem',
    cursor: 'pointer',
  },
  datepicker: {
    '& .MuiDialogActions-root': {
      // display: 'none'
      justifyContent: 'center',
      '& .MuiButtonBase-root': {
        margin: 'auto',
      },
    },
  },
  inline: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    '& .MuiTextField-root': {
      flex: 1,
    },
    '& > label': {
      position: 'relative',
      transform: 'none',
      alignItems: 'center',
      display: 'flex',
      width: (props) => (!isNil(props.labelSize) ? `${props.labelSize}px` : '200px'),
      color: '#414141',
    },
  },
  input: {
    '& input': {
      padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px`,
      fontWeight: 600,
      fontSize: 12,
    },
    '&.Mui-disabled': {
      background: '#f2f2f2',
    },
    '& > fieldset': {
      borderRadius: (props) => (props.inline ? 2 : 4),
    },
  },
  stacked: {
    display: 'flex',
    flexDirection: 'column',
    '& .MuiTextField-root': {
      flex: 1,
    },
    '& > label': {
      position: 'relative',
      transform: 'none',
      alignItems: 'center',
      display: 'flex',
      color: '#414141',
      paddingBottom: theme.spacing(0.625),
    },
  },
  readonlyText: {
    padding: (props) =>
      props.inline ? `${theme.spacing(0.625)}px 0` : `${theme.spacing(1.875)}px 0 ${theme.spacing(0.625)}px`,
    width: '100%',
    flex: 1,
  },
  cursor: {
    cursor: (props) => (props.keyboard ? 'text' : 'pointer'),
    '& .MuiInputBase-root, input': {
      cursor: (props) => (props.keyboard ? 'text' : 'pointer'),
    },
  },
}));

const MyDatePicker = (props) => {
  const classes = useStyles(props);
  const {
    maxDate,
    minDate,
    readOnly,
    inline,
    labelPosition,
    labelSize,
    format = DEFAULT_DATE_FORMAT,
    onChange,
    value: propsValue,
    // Validate min max value by react-hook-form (true) or material-ui (false)
    // Context: Sometime we need to custom message error and block save form action
    // Ex: Start Date & End Date (src\app\main\patient\components\MedicationProfiles\CreateOrUpdatePrescription.js)
    hookValidate = false,
    // MC-173679: in Pack Creator (former is Print Foil) will use DatePicker instead of KeyboardDatePicker
    keyboard = true,
    bold = true,
    showTodayButton = true,
    placeholder = null,
    noValidate = false,
    required = false,
    ...rest
  } = props;

  const [value, setValue] = useState(null);
  const [open, setOpen] = useState(false);
  const [valid, setValid] = useState(true); // Validate format
  const { parseToPharmacyTimezone, parseToLocalTimezone } = usePharmacyTimezone();
  const segmentInput = useEnhanceDateInput({
    dateFormat: format,
    value,
    isRequired: required,
    onCommit: handleTypedDate,
    onClear: clearDate,
    onInvalid: markInvalid,
  });

  const newMaxDate = maxDate == null || maxDate === ZERO_DATE ? undefined : maxDate;
  const newMinDate = minDate == null || minDate === ZERO_DATE ? undefined : minDate;

  useEffect(() => {
    if (isValid(propsValue)) {
      const tempPropsValue = parseToLocalTimezone(propsValue);

      setValue(tempPropsValue);

      if (propsValue instanceof Date) {
        /**
         * Call handleChange to keep sync value with react-hook-form
         * so we do not care about timezone, ...
         * just using `new Date()` to initialize default value
         * Ex:
         * + swapDate (brandSwap\store\reducers\brandSwapByDrug.reducer.js) in Brand Swap
         * + startDate (patient\store\reducers\update.prescriptions.reducers.js) in Medication Profile - Prescription
         */
        handleChange(tempPropsValue, dfFormat(tempPropsValue, format));
      }
    } else if (valid && !noValidate) {
      setValue(null);
    }
  }, [propsValue, onChange]);

  function commitDate(nextDate) {
    /* eslint-disable no-unused-expressions */
    setValid(true);
    setValue(nextDate);
    if (format === DEFAULT_DATE_FORMAT) {
      onChange && onChange(parseToPharmacyTimezone(nextDate).toISOString());
    } else {
      onChange && onChange(new Date(nextDate).toISOString());
    }
    /* eslint-enable no-unused-expressions */
  }

  function handleChange(date, stringDate = null) {
    /* eslint-disable no-unused-expressions */
    if (!keyboard) {
      if (!isValid(date) && !noValidate) {
        setValue(date);
        setValid(false);
        onChange && onChange(null);
        return;
      }
      if (!isDateValid(date)) {
        setValid(false);
        setValue(null);
        onChange && onChange(null);
        return;
      }
      commitDate(date);
      return;
    }

    // Keyboard mode: typing is handled by useEnhanceDateInput; this path is calendar picks and props sync
    if (isNil(date) || isNil(stringDate)) {
      clearDate();
      return;
    }
    if (!isDateValid(date) || (!noValidate && !isValid(date))) {
      setValid(false);
      return;
    }
    commitDate(date);
    /* eslint-enable no-unused-expressions */
  }

  function handleTypedDate(date) {
    if (!noValidate && !isValid(date)) {
      markInvalid();
      return;
    }
    commitDate(date);
  }

  function clearDate() {
    /* eslint-disable no-unused-expressions */
    setValue(null);
    setValid(true); // Should show old error if have
    onChange && onChange(null);
    /* eslint-enable no-unused-expressions */
  }

  function markInvalid() {
    /* eslint-disable no-unused-expressions */
    setValid(false);
    onChange && onChange(null);
    /* eslint-enable no-unused-expressions */
  }

  function handleTodayClick() {
    /* eslint-disable no-unused-expressions */
    // date is valid
    setValid(true);
    setOpen(false);
    if (format === DEFAULT_DATE_FORMAT) {
      onChange && onChange(parseToPharmacyTimezone(new Date()).toISOString());
    } else {
      onChange && onChange(new Date().toISOString());
    }
    /* eslint-enable no-unused-expressions */
  }

  const renderControl = ({ showLabel }) => {
    const Component = keyboard ? KeyboardDatePicker : DatePicker;
    const isDefaultInput = !inline && labelPosition !== 'stacked';
    return (
      <>
        {/* MC-898977: Add more hidden input to track (by useWarning) date is changed or not */}
        <Input
          type="hidden"
          className="hidden"
          name={rest.name}
          value={isDateValid(value) ? dfFormat(value, format) : ''}
        />
        <Component
          InputProps={{
            autoComplete: 'off',
            className: !isDefaultInput ? classes.input : null,
            endAdornment: (
              <InputAdornment position="end">
                <Event className={clsx(classes.icon)} />
              </InputAdornment>
            ),
          }}
          /**
					From ticket MC-485752, user can input value by keyboard.
					When validate, we have 2 case: KeyboardDatePicker validate itself & react-hook-form validate.
					In case "KeyboardDatePicker validate itself" we will omit "error" & "helperText" prop, let KeyboardDatePicker control these props.
					In case "react-hook-form validate" we will let react-hook-form control "error" & "helperText" prop.
				 */
          {...(valid && hookValidate
            ? rest
            : omitBy(rest, (v, k) => {
                if (k === 'error' && isNil(rest?.helperText)) return true;
                if (k === 'helperText' && isNil(v)) return true;
                return false;
              }))}
          label={showLabel ? rest.label : ''}
          required={required}
          autoOk
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          cancelLabel={null}
          okLabel={null}
          maxDate={newMaxDate}
          minDate={newMinDate}
          format={format}
          placeholder={placeholder ?? format.toLowerCase()}
          {...(keyboard ? { TextFieldComponent: DateSegmentField, segmentInput } : {})}
          value={value}
          onChange={handleChange}
          DialogProps={{
            className: clsx(classes.datepicker),
          }}
          showTodayButton={showTodayButton}
          todayLabel={<span onClick={handleTodayClick}>Today</span>}
        />
      </>
    );
  };

  const valueText = toDateString(value) ? toDateString(value) : '-';

  if (labelPosition === 'stacked') {
    return (
      <FormControl fullWidth className={clsx(classes.stacked, classes.cursor)}>
        <InputLabel required={required}>{rest.label}</InputLabel>
        {!readOnly && renderControl({ showLabel: false })}
        {readOnly && <Typography className={clsx(classes.readonlyText, 'cursor-default')}>{valueText}</Typography>}
      </FormControl>
    );
  }

  if (inline) {
    return (
      <FormControl fullWidth className={clsx(classes.inline, classes.cursor)}>
        <InputLabel required={required} className={clsx(bold && 'font-bold')}>
          {rest.label}
        </InputLabel>
        {!readOnly && renderControl({ showLabel: false })}
        {readOnly && <Typography className={clsx(classes.readonlyText, 'cursor-default')}>{valueText}</Typography>}
      </FormControl>
    );
  }

  if (readOnly) {
    return <TextField readOnly label={rest.label} className="cursor-default" value={valueText} />;
  }

  return <div className={clsx(classes.cursor)}>{renderControl({ showLabel: true })}</div>;
};

MyDatePicker.defaultProps = {
  keyboard: true,
};

export default MyDatePicker;
